import ApiService from "./api.service";
import { getCurrentIdToken } from "../firebase/firebaseClient";

export interface HailingOrderRequest {
  quoteId: string;
  preferVehicleClasses: string[];
  paymentInstrumentId: string;
  operatingAreas?: string[];
}

export interface HailingOrderResponse {
  hailingRequestId?: string;
}

export interface HailingOrderSnapshot {
  status: string;
  tripEstimation?: {
    encodedPolyline: string | null;
  } | null;
  driver: {
    name: string | null;
    nameTc: string | null;
    phoneNumber?: string | null;
    operatingArea?: string | null;
  } | null;
  vehicle: {
    licensePlate: string | null;
    make: string | null;
    model: string | null;
    displayName: string | null;
    displayNameTc: string | null;
    imageUrl?: string | null;
  } | null;
  live: {
    eta: number | null;
    driverLocation?: { lat: number; lng: number } | null;
  } | null;
  request?: { time?: string | null } | null;
  /** What the rider pays as things stand, boost included. */
  userTotal?: number | null;
  /** The bill, once there is one (null while pending): the rider's total boost and charge. */
  billing?: {
    boostAmount?: number | null;
    total?: number | null;
  } | null;
  /** The fare range while pending; each entry carries the rider's total boost so far. */
  matching?: {
    minMaxFareCalculations?: Array<{ boostAmount?: number | null }> | null;
  } | null;
}

export interface ActiveHailingVehicleClassOption {
  key: string;
  name: string | null;
  nameTc: string | null;
  description: string | null;
  descriptionTc: string | null;
  imageUrl: string | null;
  numberOfSeat: number | null;
  numberOfLuggage: number | null;
}

export interface ActiveHailingOrderSnapshot {
  id: string;
  hailingRequestId: string | null;
  status: string;
  createdAt: string;
  tripEstimation: {
    encodedPolyline: string | null;
  } | null;
  userTotal: number | null;
  isFixedPrice: boolean;
  itinerary: Array<{
    lat: number;
    lng: number;
    placeId: string | null;
    i18n: {
      en?: { displayName: string; formattedAddress: string };
      zhHK?: { displayName: string; formattedAddress: string };
    } | null;
  }>;
  payment: {
    cardType: string | null;
    cardSuffix: string | null;
  } | null;
  /** LIVE or SCHEDULED. */
  type?: string | null;
  request: {
    preferenceMatrix: Record<string, string[]>;
    operatingAreas: string[];
    time?: string | null;
  };
  matching: {
    selectedVehicleClassOptions: ActiveHailingVehicleClassOption[];
    lowestVehicleClassOption: ActiveHailingVehicleClassOption | null;
    minMaxFareCalculations: Array<{
      total: number;
      discountedTotal: number;
      estimatedFareFee: number;
      fixedPrice: number | null;
    }>;
  } | null;
  live: {
    driverLocation: { lat: number; lng: number } | null;
    eta: number | null;
  } | null;
  driver: {
    operatingArea: string | null;
  } | null;
}

/**
 * One row of the rider's ride history (GET /v3/me/transaction-history, DASH-6348), as
 * DashOrder. `id` is the row's own transaction id; `hailingRequestId` is null on a
 * street-meter trip, which has no hail.
 */
export interface TripHistoryOrder {
  id: string;
  hailingRequestId: string | null;
  status: string;
  createdAt: string | null;
  userTotal: number | null;
  itinerary: ActiveHailingOrderSnapshot["itinerary"] | null;
  driver: { name: string | null; nameTc: string | null } | null;
  vehicle: {
    licensePlate: string | null;
    make: string | null;
    model: string | null;
  } | null;
  billing?: { tripTotal?: number | null; total?: number | null } | null;
  payment: { cardType: string | null; cardSuffix: string | null } | null;
  request?: { time?: string | null } | null;
}

export interface TripHistoryPage {
  items: TripHistoryOrder[];
  /** The rider's full row count, not this page's. */
  total: number;
  limit: number;
}

export class MeHailingOrderService extends ApiService {
  constructor() {
    super(process.env.REACT_APP_api_endpoint!);
  }

  async createOrder(
    body: HailingOrderRequest,
  ): Promise<HailingOrderResponse | undefined> {
    if (
      !body.quoteId ||
      body.preferVehicleClasses.length === 0 ||
      !body.paymentInstrumentId
    ) {
      throw new Error(
        "A quote, selected vehicle class, and payment instrument are required to create a hailing order.",
      );
    }

    const idToken = await getCurrentIdToken();
    if (!idToken) {
      throw new Error("Rider authentication is required to create a hailing order.");
    }

    const { data } = await this.post<HailingOrderResponse | undefined>(
      "/v3/me/hailing/order",
      body,
      { headers: { Authorization: `Bearer ${idToken}` } },
    );
    return data;
  }

  async getOrder(hailingRequestId: string): Promise<HailingOrderSnapshot> {
    if (!hailingRequestId) {
      throw new Error("A hailing request ID is required to retrieve an order.");
    }

    const idToken = await getCurrentIdToken();
    if (!idToken) {
      throw new Error("Rider authentication is required to retrieve a hailing order.");
    }

    const { data } = await this.get<HailingOrderSnapshot>(
      `/v3/me/hailing/users/hails/${encodeURIComponent(hailingRequestId)}`,
      { headers: { Authorization: `Bearer ${idToken}` } },
    );
    return data;
  }

  async getActiveOrders(): Promise<ActiveHailingOrderSnapshot[]> {
    const idToken = await getCurrentIdToken();
    if (!idToken) {
      throw new Error("Rider authentication is required to retrieve active hailing orders.");
    }

    const { data } = await this.get<ActiveHailingOrderSnapshot[]>(
      "/v3/me/hailing/users/hails",
      { headers: { Authorization: `Bearer ${idToken}` } },
    );
    return data;
  }

  /** The rider's cancellation fee for this hail right now; 0 means free to cancel. */
  async getCancellationFee(hailingRequestId: string): Promise<number> {
    if (!hailingRequestId) {
      throw new Error("A hailing request ID is required to check the cancellation fee.");
    }

    const idToken = await getCurrentIdToken();
    if (!idToken) {
      throw new Error("Rider authentication is required to check the cancellation fee.");
    }

    const { data } = await this.get<{ cancellationFee: number }>(
      `/v3/me/hailing/users/hails/${encodeURIComponent(hailingRequestId)}/cancellation_fee`,
      { headers: { Authorization: `Bearer ${idToken}` } },
    );
    return data.cancellationFee;
  }

  /** One page of the rider's past and current rides, newest first. */
  async getTripHistory(args: { take: number; skip: number }): Promise<TripHistoryPage> {
    const idToken = await getCurrentIdToken();
    if (!idToken) {
      throw new Error("Rider authentication is required to read the trip history.");
    }

    const { data } = await this.get<TripHistoryPage>("/v3/me/transaction-history", {
      headers: { Authorization: `Bearer ${idToken}` },
      params: { take: args.take, skip: args.skip },
    });
    return data;
  }

  /**
   * Adds `amount` to the hail's boost and answers the order as the boost left it. A boost is
   * cumulative and cannot be taken back; it is refused once a driver accepts (HL_003) and past
   * the configured total cap (HL_002).
   */
  async boostOrder(hailingRequestId: string, amount: number): Promise<HailingOrderSnapshot> {
    if (!hailingRequestId) {
      throw new Error("A hailing request ID is required to boost an order.");
    }

    const idToken = await getCurrentIdToken();
    if (!idToken) {
      throw new Error("Rider authentication is required to boost a hailing order.");
    }

    const { data } = await this.patch<HailingOrderSnapshot>(
      `/v3/me/hailing/users/hails/${encodeURIComponent(hailingRequestId)}/boost`,
      { boostAmount: amount },
      { headers: { Authorization: `Bearer ${idToken}` } },
    );
    return data;
  }

  async cancelOrder(hailingRequestId: string): Promise<void> {
    if (!hailingRequestId) {
      throw new Error("A hailing request ID is required to cancel an order.");
    }

    const idToken = await getCurrentIdToken();
    if (!idToken) {
      throw new Error("Rider authentication is required to cancel a hailing order.");
    }

    await this.post<void>(
      `/v2/me/hailing/${encodeURIComponent(hailingRequestId)}/cancel`,
      undefined,
      { headers: { Authorization: `Bearer ${idToken}` } },
    );
  }
}
